import {Link, useSearchParams} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button'
import {Input} from '@/components/ui/input'
import {Tag} from '@/components/ui/tag'
import {PorQue} from '@/components/ui/por-que'
import {HelpIcon} from '@/components/help-icon'
import {Skeleton} from '@/components/ui/skeleton'
import {PaginationBar} from '@/components/ui/pagination-bar'
import {DataTable, type Column} from '@/components/data-table'
import {CeldaActo, CeldaEstado} from '@/components/ui/celdas-tabla'
import {FiltrosDeEstado, type OpcionEstado} from '@/components/ui/filtros-de-estado'
import {useCapabilities} from '@/hooks/empresa/organizacion/use-businesses'
import {useIsMobile} from '@/hooks/common/use-media-query'
import {useDebouncedValue} from '@/hooks/common/use-debounced-value'
import {
  QUIEN_ENTRA_PAGE_SIZE,
  useQuienEntraHoy,
  type EstadoQuienEntra,
  type FilaQuienEntra,
  type QuienEntraHoy as Lista,
} from '@/hooks/empresa/obras/use-ingreso'
import {translateApiError} from '@/lib/errors'
import {formatInstantDate} from '@/lib/risk'
import {tope} from '@/lib/campos-texto'

/** E10-H17. No autoriza ni bloquea: el único acto es abrir la decisión. El orden lo fija el
 * servidor, así que no hay selector de orden. */
export function QuienEntraHoy({businessId, proyectoId}: {businessId: string; proyectoId: string}) {
  const {t} = useTranslation()
  // En la URL y no en `useState`: al volver de decidir sobre una persona el componente se remonta.
  const [params, setParams] = useSearchParams()
  const contratista = params.get('contratista') ?? ''
  const cargo = params.get('cargo') ?? ''
  const estado = (params.get('estado') ?? 'todos') as EstadoQuienEntra
  const page = Number(params.get('page')) || 1
  // Cada request recalcula la hoja de todos los asignados: sin debounce, uno por pulsación.
  const contratistaQ = useDebouncedValue(contratista, 300)
  const cargoQ = useDebouncedValue(cargo, 300)
  const puedeFirmar = useCapabilities(businessId).sgsst_firmar
  // «Pendientes» se monta o no, sin esconderla con CSS: bajo `table-fixed` el `<colgroup>`
  // le reservaría el ancho igual. En el teléfono sus estampillas van debajo de la fila.
  const enTelefono = useIsMobile()
  const {data, isLoading, isError, error} = useQuienEntraHoy(businessId, proyectoId, {
    contratista: contratistaQ,
    cargo: cargoQ,
    estado,
    page,
  })

  function fijar(clave: string, valor: string) {
    setParams(
      (previos) => {
        if (valor) previos.set(clave, valor)
        else previos.delete(clave)
        if (clave !== 'page') previos.delete('page')
        return previos
      },
      {replace: true}
    )
  }

  function limpiar() {
    setParams(
      (previos) => {
        previos.delete('contratista')
        previos.delete('cargo')
        previos.delete('estado')
        previos.delete('page')
        return previos
      },
      {replace: true}
    )
  }

  if (isLoading && !data) return <Skeleton className="h-64 w-full" data-testid="quien-entra-cargando" />
  if (isError) {
    return (
      <p className="notice-amber max-w-2xl rounded-lg px-3 py-2 text-sm" data-testid="quien-entra-error">
        {translateApiError(error)}
      </p>
    )
  }
  if (!data) return null

  return (
    <div className="space-y-4" data-testid="quien-entra-hoy">
      <p className="text-sm text-muted-foreground" data-testid="quien-entra-no-decide">
        {t('ingreso.franja.noDecide')}
      </p>

      <div className="surface flex flex-wrap items-center gap-2 rounded-lg px-4 py-3" data-testid="quien-entra-franja">
        <FiltrosDeEstado
          opciones={opciones(data, t)}
          valor={estado}
          onValor={(siguiente) => fijar('estado', siguiente === 'todos' ? '' : siguiente)}
          testIdPrefijo="quien-entra-estado"
        />
        <HelpIcon
          label={t('common.help.iconLabelFor', {campo: t('ingreso.franja.titulo')})}
          testId="quien-entra-franja-criterio">
          {t('ingreso.franja.comoSeRehacen')}
        </HelpIcon>
      </div>

      <div className="flex flex-col gap-2 sm:flex-row">
        <Input
          maxLength={tope('BUSCA')}
          className="flex-1"
          aria-label={t('ingreso.filtro.contratista')}
          value={contratista}
          onChange={(event) => fijar('contratista', event.target.value)}
          placeholder={t('ingreso.filtro.contratistaPlaceholder')}
          data-testid="quien-entra-filtro-contratista"
        />
        <Input
          maxLength={tope('CARGO')}
          className="flex-1"
          aria-label={t('ingreso.filtro.cargo')}
          value={cargo}
          onChange={(event) => fijar('cargo', event.target.value)}
          placeholder={t('ingreso.filtro.cargoPlaceholder')}
          data-testid="quien-entra-filtro-cargo"
        />
      </div>

      <div data-testid="quien-entra-filas">
        <DataTable
          columns={columnas({
            sinPeligros: data.sin_peligros,
            enTelefono,
            puedeFirmar,
            businessId,
            proyectoId,
            volver: params.toString(),
            t,
          })}
          rows={data.items}
          rowKey={(fila) => fila.employee_id}
          renderRowDetail={enTelefono ? (fila) => <Cuentas fila={fila} t={t} /> : undefined}
          emptyState={<Vacio data={data} businessId={businessId} proyectoId={proyectoId} onLimpiar={limpiar} />}
        />
      </div>

      <PaginationBar
        page={data.page}
        pageSize={QUIEN_ENTRA_PAGE_SIZE}
        total={data.total}
        onPageChange={(siguiente) => fijar('page', String(siguiente))}
      />
    </div>
  )
}

type Traducir = (clave: string, opciones?: Record<string, unknown>) => string

/** `franja_visible` y no `franja`: respeta las búsquedas, así el conteo es lo que aparece al tocar. */
function opciones(data: Lista, t: Traducir): OpcionEstado<EstadoQuienEntra>[] {
  return [
    {valor: 'todos', etiqueta: t('ingreso.filtro.estado.todos'), conteo: data.franja_visible.asignados},
    {
      valor: 'no_habilitados',
      etiqueta: t('ingreso.filtro.estado.noHabilitados'),
      conteo: data.franja_visible.no_habilitados,
    },
    {
      valor: 'sin_verificar',
      etiqueta: t('ingreso.filtro.estado.sinVerificar'),
      conteo: data.franja_visible.sin_verificar,
    },
    {
      valor: 'habilitados',
      etiqueta: t('ingreso.filtro.estado.habilitados'),
      conteo: data.franja_visible.habilitados,
    },
  ]
}

/** Orden de H17: 🔴 → 🟡 → no pude verificar → al día; 🔵 y gris aparte. */
const TONO_POR_PRIORIDAD = ['red', 'amber', 'neutral', 'green', 'enCurso'] as const

function columnas({
  sinPeligros,
  enTelefono,
  puedeFirmar,
  businessId,
  proyectoId,
  volver,
  t,
}: {
  sinPeligros: boolean
  enTelefono: boolean
  puedeFirmar: boolean
  businessId: string
  proyectoId: string
  volver: string
  t: Traducir
}): Column<FilaQuienEntra>[] {
  return [
    {
      key: 'persona',
      header: t('ingreso.columna.persona'),
      // Con filas, «sin peligros» va acá y no en una franja (U3); primera columna porque es la
      // única que existe en los dos anchos.
      headerAside: sinPeligros ? (
        <HelpIcon
          label={t('common.help.iconLabelFor', {campo: t('ingreso.columna.persona')})}
          testId="quien-entra-sin-peligros">
          {t('ingreso.sinPeligrosCorto')}
        </HelpIcon>
      ) : undefined,
      cell: (fila) => (
        <CeldaEstado
          chip={<Tag tone={TONO_POR_PRIORIDAD[fila.prioridad]}>{t(`ingreso.prioridad.${fila.prioridad}`)}</Tag>}
          nombre={fila.nombre}
          meta={
            <>
              {fila.cargo ?? t('ingreso.fila.sinCargo')}
              {fila.es_contratista && ` · ${fila.contratista_empresa ?? t('ingreso.fila.contratistaSinEmpresa')}`}
            </>
          }
          porque={porqueDeLaFila(fila, t)}
          porqueTestId={`quien-entra-porque-${fila.employee_id}`}
        />
      ),
    },
    ...(enTelefono
      ? []
      : [
          {
            key: 'cuentas',
            header: t('ingreso.columna.cuentas'),
            width: 'w-56',
            cell: (fila: FilaQuienEntra) => <Cuentas fila={fila} t={t} />,
          },
        ]),
    {
      key: 'acto',
      header: t('ingreso.columna.acto'),
      width: 'w-36',
      className: 'text-right',
      stopClick: true,
      cell: (fila) => (
        <CeldaActo
          // Decidir es de firmante: para un operador la fila queda en lectura.
          acto={
            puedeFirmar && (
              <Button asChild size="sm" variant="outline" data-testid={`quien-entra-abrir-${fila.employee_id}`}>
                <Link
                  to={`/businesses/${businessId}/obras/${proyectoId}/ingreso/${fila.employee_id}?volver=${encodeURIComponent(volver)}`}>
                  {t('ingreso.fila.abrir')}
                </Link>
              </Button>
            )
          }
        />
      ),
    },
  ]
}

function porqueDeLaFila(fila: FilaQuienEntra, t: Traducir) {
  return [
    t('ingreso.fila.cuentas', {
      rojos: fila.rojos,
      porVencer: fila.por_vencer,
      noVerificables: fila.no_verificables,
    }),
    fila.sin_labor ? t('ingreso.fila.sinLabor') : '',
  ]
    .filter(Boolean)
    .join(' · ')
}

/** Sin cuentas se pinta «nada pendiente»: una celda vacía se leería como un dato que falta. */
function Cuentas({fila, t}: {fila: FilaQuienEntra; t: Traducir}) {
  const cuentas = [
    {clave: 'rojos', tono: 'red', valor: fila.rojos},
    {clave: 'porVencer', tono: 'amber', valor: fila.por_vencer},
    {clave: 'sinVerificar', tono: 'neutral', valor: fila.no_verificables},
  ] as const
  const conAlgo = cuentas.filter((cuenta) => cuenta.valor > 0)
  return (
    <div className="space-y-1">
      <div className="flex flex-wrap gap-1" data-testid={`quien-entra-cuentas-${fila.employee_id}`}>
        {conAlgo.length === 0 ? (
          <Tag tone="green">{t('ingreso.cuenta.nada')}</Tag>
        ) : (
          conAlgo.map((cuenta) => (
            <Tag key={cuenta.clave} tone={cuenta.tono}>
              {t(`ingreso.cuenta.${cuenta.clave}`, {cuantas: cuenta.valor})}
            </Tag>
          ))
        )}
      </div>
      {/* P-E10-5: sin color, no se da por vigente. Es un instante UTC: recortarlo daría el día
          siguiente en la tarde de Bogotá. */}
      {fila.ultima_decision_en && (
        <div className="text-xs text-muted-foreground" data-testid={`quien-entra-ultima-${fila.employee_id}`}>
          {t('ingreso.fila.ultimaDecision', {
            fecha: formatInstantDate(fila.ultima_decision_en),
            veredicto: t(`ingreso.ultimaVeredicto.${fila.ultima_decision_autorizada}`),
          })}
        </div>
      )}
    </div>
  )
}

/** Único sitio de la franja «sin peligros»: sin filas no hay estampilla que lo diga (U3). */
function Vacio({
  data,
  businessId,
  proyectoId,
  onLimpiar,
}: {
  data: Lista
  businessId: string
  proyectoId: string
  onLimpiar: () => void
}) {
  const {t} = useTranslation()
  return (
    <div className="space-y-3">
      {data.sin_peligros && (
        <div className="notice-amber space-y-1 rounded-lg px-3 py-2 text-sm" data-testid="quien-entra-sin-peligros">
          <p>{t('ingreso.sinPeligros')}</p>
          <PorQue label={t('common.porQue')} testId="quien-entra-sin-peligros-porque">
            <p>{t('ingreso.sinPeligrosPorque')}</p>
          </PorQue>
        </div>
      )}
      <p className="text-sm text-muted-foreground" data-testid="quien-entra-vacio">
        {data.franja.asignados === 0 ? (
          <>
            {t('ingreso.vacio.sinGente')}{' '}
            <Link className="underline" to={`/businesses/${businessId}/obras/${proyectoId}`}>
              {t('ingreso.vacio.asignar')}
            </Link>
          </>
        ) : (
          <>
            {t('ingreso.vacio.sinCoincidencias')}{' '}
            <button type="button" className="underline" onClick={onLimpiar} data-testid="quien-entra-limpiar">
              {t('ingreso.vacio.limpiarFiltros')}
            </button>{' '}
            <PorQue label={t('common.porQue')} testId="quien-entra-vacio-porque">
              <p>{t('ingreso.vacio.sinCoincidenciasPorque')}</p>
            </PorQue>
          </>
        )}
      </p>
    </div>
  )
}
